import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Expand, FileDown, FileUp, Info, Keyboard, List, Loader2, MessageCircle, MousePointer2, MousePointerClick, Paperclip,
  ScreenShare, Type, X,
} from 'lucide-react';
import { Modal } from '../Modal';
import { ConfirmDialog } from '../ConfirmDialog';
import { ContextMenu, ContextMenuItem } from '../ContextMenu';
import { useToast } from '../../contexts/ToastContext';
import { RsPeer } from '../../services/remoteSupportService';
import {
  MainToViewer,
  ViewerCommand,
  ViewerLink,
  ViewerSnapshot,
  ViewerToMain,
  closeViewerSelf,
  getRemoteViewerNonce,
  markViewerCloseHandled,
  openViewerLink,
} from '../../services/remoteViewerLink';
import { resolveAvatarUrl } from '../../utils/serverUrl';
import { isTauri } from '../../utils/tauri';
import { handleAvatarError } from '../../types';
import {
  MAX_SEND_TEXT,
  RemoteInputEvent,
  SPECIAL_KEYS,
  comboEvents,
  monitorLabel,
  monitorViews,
  textEvents,
} from '../../utils/remoteInput';
import { endReasonLabel, filesLabel, formatDuration } from '../../utils/remoteSupportLabels';
import { RemoteScreenViewer } from './RemoteScreenViewer';
import { RemoteFilePanel, fileAvailHint, useRemoteFileOperator } from './RemoteFilePanel';

/**
 * 원격지원 담당자 — 원격 전용 창(`?rsviewer=<nonce>`). 채팅 앱(메인 창)과는 BroadcastChannel로만 이야기한다.
 *
 *  - 세션 상태는 메인의 스냅샷을 그대로 그린다(소켓·상태 머신은 메인 RemoteSupportOperator 소유)
 *  - RsPeer는 이 창이 가진다 — 시그널만 메인이 소켓으로 중계한다
 *  - 메인이 사라지면(bye·ping 무응답) 피어를 닫고 '연결이 끊겼다'를 띄운다. 서버는 담당자 소켓이 끊기면 세션을 끝낸다
 *
 * ⚠ App이 모든 훅보다 먼저 이 화면으로 분기한다. 이 창(realm)에는 RemoteSupportHost를 마운트하면 안 된다 —
 *   부팅 1회 rs_session_end가 같은 PC에서 진행 중인 직원 쪽 세션의 테두리·게이트를 걷어 버린다.
 */

const PING_MS = 3000;
/** 연달아 이만큼 pong이 없으면 메인이 사라진 것으로 본다 */
const MISSES_TO_DISCONNECT = 2;
const NOTE_MAX = 1000;

export const RemoteViewerWindow: React.FC = () => {
  // App이 nonce를 확인하고 이 화면을 렌더한다
  const nonce = getRemoteViewerNonce() || '';
  const { showToast } = useToast();
  const [snap, setSnap] = useState<ViewerSnapshot | null>(null);
  const [disconnected, setDisconnected] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [channelsOpen, setChannelsOpen] = useState(false);
  const [connState, setConnState] = useState<RTCPeerConnectionState | 'new'>('new');
  const [showInfo, setShowInfo] = useState(false);
  const [fit, setFit] = useState<'contain' | 'actual'>('contain');
  /** 모니터 하나 확대(null = 전체). 직원 PC는 모든 모니터를 한 프레임으로 보낸다 */
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [keysMenu, setKeysMenu] = useState<{ x: number; y: number } | null>(null);
  const [textOpen, setTextOpen] = useState(false);
  const [textValue, setTextValue] = useState('');
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  /** 파일 주고받기 — 'fx' 채널(RsPeer가 넘겨준다), 오른쪽 패널, [파일] 메뉴 */
  const [fileChannel, setFileChannel] = useState<RTCDataChannel | null>(null);
  const [showFiles, setShowFiles] = useState(false);
  const [fileMenu, setFileMenu] = useState<{ x: number; y: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const linkRef = useRef<ViewerLink<ViewerToMain> | null>(null);
  const peerRef = useRef<RsPeer | null>(null);
  const snapRef = useRef<ViewerSnapshot | null>(null);
  snapRef.current = snap;
  const disconnectedRef = useRef(false);
  disconnectedRef.current = disconnected;
  const lastPingAt = useRef(0);
  const lastPongAt = useRef(0);
  const misses = useRef(0);

  const post = useCallback((msg: ViewerToMain) => linkRef.current?.post(msg), []);

  const closePeer = useCallback(() => {
    peerRef.current?.close();
    peerRef.current = null;
    setStream(null);
    setChannelsOpen(false);
    setFileChannel(null);
    setConnState('new');
  }, []);

  const ensurePeer = useCallback(() => {
    if (peerRef.current) return peerRef.current;
    const peer = new RsPeer('operator', {
      onSignal: (data) => post({ type: 'signal', data }),
      onTrack: (s) => setStream(s),
      onChannelsChange: (open) => setChannelsOpen(open),
      onConnectionState: (state) => setConnState(state),
      onFileChannel: (ch) => setFileChannel(ch),
    });
    peerRef.current = peer;
    return peer;
  }, [post]);

  const closeOverlays = useCallback(() => {
    setConfirmEnd(false);
    setTextOpen(false);
    setKeysMenu(null);
    setFileMenu(null);
  }, []);

  const goDisconnected = useCallback(() => {
    setDisconnected(true);
    closePeer();
    closeOverlays();
  }, [closePeer, closeOverlays]);

  // ── 메인 창 링크 ──

  const onMessage = (msg: MainToViewer) => {
    if (disconnectedRef.current) return; // 한 번 끊기면 되살리지 않는다(피어가 이미 닫혔다)
    switch (msg.type) {
      case 'state': {
        const s = msg.snap;
        setSnap(s);
        if (s.phase === 'viewing') ensurePeer();
        if (s.phase === 'ended') {
          closePeer();
          closeOverlays();
        } else if (s.control !== 'granted') {
          setTextOpen(false);
          setKeysMenu(null);
        }
        return;
      }
      case 'signal':
        void ensurePeer().handleSignal(msg.data);
        return;
      case 'toast':
        showToast({ type: msg.toastType, message: msg.message });
        return;
      case 'close-requested': {
        post({ type: 'close-ack' });
        markViewerCloseHandled();
        if (snapRef.current?.phase === 'viewing') setConfirmEnd(true);
        else closeViewerSelf();
        return;
      }
      case 'note-result':
        if (msg.ok) {
          closeViewerSelf();
        } else {
          setSavingNote(false);
          showToast({ type: 'error', message: msg.message || '메모를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
        }
        return;
      case 'pong':
        lastPongAt.current = Date.now();
        misses.current = 0;
        return;
      case 'bye':
        goDisconnected();
        return;
      default:
    }
  };
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    // 링크는 페이지 동안 하나다 — 다시 열면 hello가 또 가고, 메인은 그것을 '창이 새로고침돼 피어를 잃었다'로 본다
    const link = openViewerLink(nonce, (msg) => onMessageRef.current(msg));
    linkRef.current = link;
    link.post({ type: 'hello' });
    const onPageHide = () => link.post({ type: 'closing' });
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      link.close();
      linkRef.current = null;
      peerRef.current?.close();
      peerRef.current = null;
    };
  }, [nonce]);

  // 메인 생존 확인 — "보낸 ping 뒤에 pong이 하나도 없으면" 놓친 것으로 센다.
  // 메인이 숨김(트레이)이어도 메시지 처리는 스로틀링되지 않으므로 메인 쪽 타이머에 기대지 않는다
  useEffect(() => {
    const t = window.setInterval(() => {
      if (disconnectedRef.current) return;
      if (lastPingAt.current && lastPongAt.current < lastPingAt.current) {
        misses.current += 1;
        if (misses.current >= MISSES_TO_DISCONNECT) {
          goDisconnected();
          return;
        }
      }
      lastPingAt.current = Date.now();
      post({ type: 'ping' });
    }, PING_MS);
    return () => window.clearInterval(t);
  }, [goDisconnected, post]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const phase = snap?.phase ?? 'opening';
  const asked = !!snap?.asked;
  const target = snap?.target ?? null;
  const viewing = phase === 'viewing' && !disconnected;

  useEffect(() => {
    document.title = target?.name ? `${target.name}님 PC · 원격지원` : '원격지원';
  }, [target?.name]);

  const files = useRemoteFileOperator(fileChannel, {
    enabled: !!snap?.fileTransferEnabled && !disconnected,
    viewing,
    inputChannelsOpen: channelsOpen,
  });
  const fileBatchCount = files.snap?.batches.length ?? 0;
  const filePendingPull = !!files.snap?.pendingPull;
  // 새 전송이 생기면 패널을 연다(PC 정보와 자리를 나눠 쓰지 않는다)
  useEffect(() => {
    if (fileBatchCount > 0 || filePendingPull) {
      setShowFiles(true);
      setShowInfo(false);
    }
  }, [fileBatchCount, filePendingPull]);

  // 웹 팝업을 그냥 닫으려 하면 브라우저 확인창(PC앱 ×는 Rust → 메인 → 이 창의 ConfirmDialog 경로)
  useEffect(() => {
    if (isTauri() || !viewing) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [viewing]);

  // ── 동작 ──

  const sendInput = useCallback(
    (kind: 'mv' | 'ev', events: RemoteInputEvent[]) => {
      if (peerRef.current?.send(kind, events)) return;
      // DataChannel이 안 열렸다(방화벽 등) — 메인이 서버 중계로 보낸다(느리다)
      post({ type: 'input', events });
    },
    [post]
  );

  const cmd = (c: ViewerCommand) => post({ type: 'cmd', cmd: c });

  const openDm = () => {
    cmd({ name: 'open-dm' });
    // 웹: 채팅 탭을 앞으로(브라우저가 막을 수 있다 — 최선 노력). PC앱은 메인이 rs_bring_to_front로 꺼낸다
    if (!isTauri()) {
      try {
        window.opener?.focus();
      } catch {
        /* 닫힘 */
      }
    }
  };

  const submitText = () => {
    const events = textEvents(textValue);
    if (events.length === 0) return;
    sendInput('ev', events);
    setTextOpen(false);
    setTextValue('');
    showToast({ type: 'success', message: '텍스트를 보냈습니다. 직원 PC에서 커서가 있던 곳에 입력됩니다.' });
  };

  const saveNote = () => {
    if (!note.trim() || savingNote) return;
    setSavingNote(true);
    cmd({ name: 'save-note', note });
  };

  // ── 렌더 ──

  const control = disconnected ? 'none' : snap?.control ?? 'none';
  const granted = control === 'granted';
  const startedAt = snap?.startedAt ?? null;
  const elapsed = startedAt ? Math.max(0, Math.round((now - startedAt) / 1000)) : 0;
  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;
  const remaining = snap?.expiresAt ? Math.max(0, Math.ceil((snap.expiresAt - now) / 1000)) : 0;
  const summary = !disconnected && phase === 'ended' ? snap?.summary ?? null : null;
  const pcInfo = snap?.pcInfo ?? null;

  // 모니터별 확대 — 직원 PC가 캡처 사각형(capture)을 알려 줄 때만(구버전 PC앱은 모니터 하나만 보내 없다)
  const capture = pcInfo?.capture ?? null;
  const monitors = pcInfo?.monitors;
  const views = useMemo(
    () => (capture && monitors && monitors.length > 1 ? monitorViews(capture, monitors) : []),
    [capture, monitors]
  );
  // 모니터를 꽂거나 빼면(직원 PC가 rs:info를 다시 보낸다) 전체 보기로 되돌린다
  const layoutKey = views.map((v) => `${v.x},${v.y},${v.w},${v.h}`).join('|');
  useEffect(() => setViewIndex(null), [layoutKey]);
  const activeView = viewIndex !== null ? views[viewIndex] ?? null : null;

  const closeButton = (
    <button
      type="button"
      onClick={closeViewerSelf}
      className="mt-4 px-4 py-2 text-sm rounded-lg border border-slate-500 hover:bg-white/10"
    >
      닫기
    </button>
  );

  const overlay = disconnected ? (
    <div className="text-white max-w-sm">
      <p className="font-semibold">채팅 앱과 연결이 끊겨 원격지원이 끝났습니다</p>
      <p className="mt-1 text-sm text-slate-400">계속하려면 채팅 앱에서 다시 요청해 주세요.</p>
      {closeButton}
    </div>
  ) : phase === 'opening' ? (
    <div className="text-white max-w-sm">
      <Loader2 size={32} className="mx-auto mb-3 animate-spin text-slate-400" aria-hidden="true" />
      <p className="font-semibold">{asked ? '요청을 수락하는 중입니다' : '원격지원을 요청하는 중입니다'}</p>
    </div>
  ) : phase === 'waiting' ? (
    <div className="text-white max-w-sm">
      <Loader2 size={32} className="mx-auto mb-3 animate-spin text-slate-400" aria-hidden="true" />
      <p className="font-semibold">
        {asked ? `${target?.name}님의 원격지원 요청을 수락했습니다` : `${target?.name}님에게 원격지원을 요청했습니다`}
      </p>
      <p className="mt-1 text-sm text-slate-400 tabular-nums">
        {asked ? '직원이 화면 공유를 누르기를 기다리는 중' : '직원이 허용하기를 기다리는 중'} · {remaining}초
      </p>
      <button
        type="button"
        onClick={() => cmd({ name: 'end' })}
        className="mt-4 px-4 py-2 text-sm rounded-lg border border-slate-500 hover:bg-white/10"
      >
        {asked ? '취소' : '요청 취소'}
      </button>
    </div>
  ) : phase === 'picking' ? (
    <div className="text-white max-w-sm">
      <ScreenShare size={32} className="mx-auto mb-3 text-slate-400" aria-hidden="true" />
      <p className="font-semibold">{target?.name}님이 화면 공유를 확인하는 중입니다</p>
      <p className="mt-1 text-sm text-slate-400">직원 PC의 확인창에서 [공유]를 누르면 바로 보입니다.</p>
    </div>
  ) : phase === 'ended' ? (
    summary ? (
      <div className="text-white max-w-sm">
        <p className="font-semibold">원격지원을 마쳤습니다</p>
      </div>
    ) : (
      <div className="text-white max-w-sm">
        <p className="font-semibold">{snap?.endedMessage || '원격지원 요청이 끝났습니다.'}</p>
        {closeButton}
      </div>
    )
  ) : !stream ? (
    <div className="text-white max-w-sm">
      {connState === 'failed' ? (
        <>
          <p className="font-semibold">직원 PC와 연결하지 못했습니다</p>
          <p className="mt-1 text-sm text-slate-400">
            사내망 밖(재택·외부망)에서는 연결되지 않을 수 있습니다. 종료한 뒤 다시 요청해 주세요.
          </p>
        </>
      ) : (
        <>
          <Loader2 size={32} className="mx-auto mb-3 animate-spin text-slate-400" aria-hidden="true" />
          <p className="font-semibold">화면을 연결하는 중입니다</p>
        </>
      )}
    </div>
  ) : undefined;

  const keyItems: ContextMenuItem[] = SPECIAL_KEYS.map((k) => ({
    key: k.id,
    label: k.label,
    onSelect: () => sendInput('ev', comboEvents(k.codes)),
  }));

  const toolBtn =
    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-slate-200 hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent transition-colors';

  const live = !disconnected && phase !== 'ended';
  const statusLine = disconnected
    ? '연결 끊김'
    : phase === 'viewing'
      ? `${granted ? '제어 중' : control === 'requested' ? '제어 요청 중' : '화면 보는 중'} · ${mmss}${snap?.code ? ` · ${snap.code}` : ''}`
      : phase === 'ended'
        ? '종료됨'
        : '연결 대기 중';

  return (
    <>
      <div className="fixed inset-0 flex flex-col bg-slate-900">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 md:px-4 py-2 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <img
              src={resolveAvatarUrl(target?.avatar)}
              onError={handleAvatarError}
              alt=""
              className="w-8 h-8 rounded-full object-cover bg-slate-700 shrink-0"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{target ? `${target.name}님 PC` : '원격지원'}</p>
              <p className="text-xs text-slate-400 tabular-nums truncate">
                {statusLine}
                {viewing && stream && !channelsOpen && granted && ' · 입력이 서버를 거쳐 전달됩니다(느릴 수 있음)'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            {viewing && snap?.controlEnabled && (
              granted ? (
                <button type="button" onClick={() => cmd({ name: 'return-control' })} className={toolBtn}>
                  <MousePointer2 size={16} aria-hidden="true" />
                  제어 반납
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => cmd({ name: 'request-control' })}
                  disabled={control === 'requested' || !stream}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50 transition-colors"
                >
                  <MousePointerClick size={16} aria-hidden="true" />
                  {control === 'requested' ? '제어 요청 중' : '제어 요청'}
                </button>
              )
            )}
            <button
              type="button"
              disabled={!granted}
              onClick={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setKeysMenu({ x: r.left, y: r.bottom + 4 });
              }}
              className={toolBtn}
              title="담당자 PC에서 누를 수 없는 조합 키"
            >
              <Keyboard size={16} aria-hidden="true" />
              특수 키
            </button>
            <button type="button" disabled={!granted} onClick={() => setTextOpen(true)} className={toolBtn}>
              <Type size={16} aria-hidden="true" />
              텍스트 보내기
            </button>
            <button
              type="button"
              disabled={!viewing}
              onClick={() => {
                setShowInfo((v) => !v);
                setShowFiles(false);
              }}
              aria-pressed={showInfo}
              className={`${toolBtn} ${showInfo ? 'bg-white/10' : ''}`}
            >
              <Info size={16} aria-hidden="true" />
              PC 정보
            </button>
            <button
              type="button"
              disabled={!viewing || files.avail !== 'ready'}
              onClick={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setFileMenu({ x: r.left, y: r.bottom + 4 });
              }}
              className={`${toolBtn} ${showFiles ? 'bg-white/10' : ''}`}
              title={fileAvailHint(files.avail)}
            >
              <Paperclip size={16} aria-hidden="true" />
              파일
            </button>
            <button type="button" disabled={!target?.userId || disconnected} onClick={openDm} className={toolBtn}>
              <MessageCircle size={16} aria-hidden="true" />
              대화 열기
            </button>
            {views.length > 1 && (
              <div role="group" aria-label="모니터 보기" className="flex items-center rounded-lg bg-white/5 p-0.5">
                {[null, ...views.map((_, i) => i)].map((i) => {
                  const on = fit === 'contain' && viewIndex === i;
                  const m = i !== null ? monitors?.[i] : undefined;
                  return (
                    <button
                      key={i === null ? 'all' : i}
                      type="button"
                      disabled={!stream}
                      aria-pressed={on}
                      onClick={() => {
                        setViewIndex(i);
                        setFit('contain');
                      }}
                      title={
                        m && monitors
                          ? `${monitorLabel(i as number, monitors.length, m.primary)} ${m.width}×${m.height}`
                          : '모든 모니터'
                      }
                      className={`px-2.5 py-1 rounded-md text-sm transition-colors disabled:opacity-40 ${
                        on ? 'bg-white/20 text-white' : 'text-slate-300 hover:bg-white/10'
                      }`}
                    >
                      {i === null ? '전체' : `화면 ${i + 1}`}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              disabled={!stream}
              onClick={() => setFit((f) => (f === 'contain' ? 'actual' : 'contain'))}
              className={toolBtn}
            >
              <Expand size={16} aria-hidden="true" />
              {fit === 'contain' ? '원본 크기' : '화면 맞춤'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!live) closeViewerSelf();
                else if (phase === 'viewing') setConfirmEnd(true);
                else cmd({ name: 'end' });
              }}
              disabled={live && phase === 'opening'}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 hover:bg-white/20 text-white disabled:opacity-50"
            >
              <X size={16} aria-hidden="true" />
              {live ? '종료' : '닫기'}
            </button>
          </div>
        </div>

        <div className="flex-1 flex min-h-0">
          <div className="flex-1 min-w-0">
            <RemoteScreenViewer
              stream={stream}
              controlActive={granted && !textOpen && !confirmEnd}
              fit={fit}
              view={activeView}
              secureDesktop={viewing && !!snap?.secureDesktop}
              moveIntervalMs={channelsOpen ? 25 : 60}
              onInput={sendInput}
              overlay={overlay}
            />
          </div>
          {showInfo && viewing && (
            <aside className="w-72 shrink-0 overflow-y-auto bg-slate-800 border-l border-slate-700 p-4 text-sm text-slate-200">
              <h3 className="mb-3 text-xs font-semibold text-slate-400">PC 정보</h3>
              {pcInfo ? (
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                  <dt className="text-slate-400">PC 이름</dt>
                  <dd className="break-anywhere">{pcInfo.computerName || '-'}</dd>
                  <dt className="text-slate-400">Windows 계정</dt>
                  <dd className="break-anywhere">{pcInfo.userName || '-'}</dd>
                  <dt className="text-slate-400">운영체제</dt>
                  <dd className="break-anywhere">
                    {pcInfo.os}
                    {pcInfo.osBuild && <span className="text-slate-400"> ({pcInfo.osBuild})</span>}
                  </dd>
                  <dt className="text-slate-400">CPU</dt>
                  <dd className="break-anywhere">{pcInfo.cpu || '-'}</dd>
                  <dt className="text-slate-400">메모리</dt>
                  <dd className="tabular-nums">
                    {(pcInfo.ramTotalMb / 1024).toFixed(1)}GB (여유 {(pcInfo.ramFreeMb / 1024).toFixed(1)}GB)
                  </dd>
                  <dt className="text-slate-400">C: 드라이브</dt>
                  <dd className="tabular-nums">
                    {pcInfo.diskTotalGb}GB (여유 {pcInfo.diskFreeGb}GB)
                  </dd>
                  <dt className="text-slate-400">IP</dt>
                  <dd className="tabular-nums">{pcInfo.ip || '-'}</dd>
                  <dt className="text-slate-400">켜진 시간</dt>
                  <dd className="tabular-nums">{pcInfo.uptimeHours}시간</dd>
                  <dt className="text-slate-400">INA-TALK</dt>
                  <dd className="tabular-nums">{pcInfo.appVersion}</dd>
                  <dt className="text-slate-400">모니터</dt>
                  <dd>
                    {pcInfo.monitors.map((m, i) => (
                      <span key={m.device} className="block tabular-nums">
                        {monitorLabel(i, pcInfo.monitors.length, m.primary)} {m.width}×{m.height}
                        {m.scalePercent !== 100 ? ` ${m.scalePercent}%` : ''}
                      </span>
                    ))}
                  </dd>
                </dl>
              ) : (
                <p className="text-xs text-slate-400">직원 PC에서 정보를 받는 중입니다</p>
              )}
              <p className="mt-4 text-xs text-slate-500">직원에게도 이 정보가 전달된다고 미리 안내됩니다.</p>
            </aside>
          )}
          {showFiles && viewing && (
            <RemoteFilePanel
              snap={files.snap}
              avail={files.avail}
              hostName={target?.name ?? '직원'}
              onClose={() => setShowFiles(false)}
              onCancel={files.cancel}
              onCancelPull={files.cancelPull}
              onDismiss={files.dismiss}
            />
          )}
        </div>
      </div>

      {keysMenu && (
        <ContextMenu x={keysMenu.x} y={keysMenu.y} items={keyItems} onClose={() => setKeysMenu(null)} />
      )}

      {fileMenu && (
        <ContextMenu
          x={fileMenu.x}
          y={fileMenu.y}
          onClose={() => setFileMenu(null)}
          items={[
            // ContextMenu는 항목을 눌러도 스스로 닫히지 않는다 — 각 항목이 닫는다.
            // 파일 선택 창은 클릭 안에서만 열린다 — onSelect가 클릭 처리 중에 불리므로 그대로 연다
            {
              key: 'send',
              label: '파일 보내기',
              icon: FileUp,
              onSelect: () => {
                setFileMenu(null);
                fileInputRef.current?.click();
              },
            },
            {
              key: 'pull',
              label: '파일 가져오기',
              icon: FileDown,
              onSelect: () => {
                setFileMenu(null);
                files.requestPull();
              },
            },
            {
              key: 'list',
              label: '주고받은 파일',
              icon: List,
              onSelect: () => {
                setFileMenu(null);
                setShowFiles(true);
                setShowInfo(false);
              },
            },
          ]}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const list = Array.from(e.target.files ?? []);
          e.target.value = '';
          files.sendFiles(list);
        }}
      />

      <Modal
        isOpen={textOpen}
        onClose={() => setTextOpen(false)}
        title="텍스트 보내기"
        size="sm"
        footer={
          <button
            type="button"
            onClick={submitText}
            disabled={!textValue}
            className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
          >
            보내기
          </button>
        }
      >
        <p className="mb-2 text-xs text-slate-500">
          직원 PC에서 커서가 있는 곳에 그대로 입력됩니다. 긴 경로나 주소를 옮길 때 쓰세요({MAX_SEND_TEXT}자까지).
        </p>
        <textarea
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          maxLength={MAX_SEND_TEXT}
          rows={4}
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
        />
      </Modal>

      <ConfirmDialog
        isOpen={confirmEnd}
        title="원격지원 종료"
        message={`${target?.name ?? ''}님 PC의 원격지원을 끝낼까요?`}
        confirmLabel="종료"
        danger
        onConfirm={() => {
          setConfirmEnd(false);
          cmd({ name: 'end' });
        }}
        onCancel={() => setConfirmEnd(false)}
      />

      <Modal
        isOpen={!!summary}
        onClose={closeViewerSelf}
        title="원격지원을 마쳤습니다"
        size="sm"
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={closeViewerSelf}
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              닫기
            </button>
            <button
              type="button"
              onClick={saveNote}
              disabled={savingNote || !note.trim()}
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
            >
              {savingNote ? '저장 중…' : '메모 저장'}
            </button>
          </div>
        }
      >
        {summary && (
          <div className="space-y-3 text-sm">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              <dt className="text-slate-500">직원</dt>
              <dd className="text-slate-800">{summary.hostName}</dd>
              <dt className="text-slate-500">총 시간</dt>
              <dd className="text-slate-800 tabular-nums">
                {formatDuration(summary.durationSeconds)}
                {summary.controlSeconds > 0 && ` (제어 ${formatDuration(summary.controlSeconds)})`}
              </dd>
              <dt className="text-slate-500">세션 번호</dt>
              <dd className="text-slate-800 tabular-nums">{summary.code}</dd>
              {filesLabel(summary.filesToHost, summary.filesToOperator, 'operator') && (
                <>
                  <dt className="text-slate-500">파일</dt>
                  <dd className="text-slate-800">{filesLabel(summary.filesToHost, summary.filesToOperator, 'operator')}</dd>
                </>
              )}
              <dt className="text-slate-500">종료</dt>
              <dd className="text-slate-800">{endReasonLabel(summary.reason)}</dd>
            </dl>
            <label className="block">
              <span className="block mb-1 text-xs font-medium text-slate-600">처리 메모 (직원도 볼 수 있습니다)</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                rows={4}
                placeholder="무엇을 확인하고 어떻게 해결했는지 적어 주세요"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
              />
            </label>
          </div>
        )}
      </Modal>
    </>
  );
};
